// AddBeneficiaryModal — shared register-a-beneficiary modal.
//
// Extracted from MerchantDetail.tsx so the new standalone /beneficiaries page
// (reachable from BOTH admin and merchant sidebars) can mount the same modal
// without duplicating the form. The previous in-merchant-detail location had
// the form, but only admins had a sidebar path to /merchants/:id — merchant
// users were stranded with no way to register a beneficiary at all. This
// component is the reusable surface that fixes that.
//
// Form fields (all required by the backend):
//   bank_account_id (selects the rail whose credentials register the bene)
//   name, account_no, ifsc, email, phone
//
// On submit it invalidates BOTH cache shapes — ["merchant-beneficiaries", id]
// (used by MerchantDetail) AND ["beneficiaries", id] (used by UploadBatchModal
// AND the new /beneficiaries page). Missing either means a freshly-added
// beneficiary doesn't appear in one surface for up to staleTime (60s).
import { useEffect, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Modal } from "@/components/UI";
import { apiErrorMessage, extractCustomerDuplicate } from "@/utils/apiError";
import { createBeneficiary } from "@/api/beneficiaries";
import type { BankAccount } from "@/api/bankAccounts";
import { providerLabel } from "@/utils/providerLabel";

interface Props {
  merchantId: number;
  rails: BankAccount[];
  open: boolean;
  onClose: () => void;
}

export function AddBeneficiaryModal({
  merchantId,
  rails,
  open,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const [bankAccountId, setBankAccountId] = useState("");
  const [name, setName] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setBankAccountId("");
      setName("");
      setAccountNo("");
      setIfsc("");
      setEmail("");
      setPhone("");
      setSaving(false);
      setError(null);
    }
  }, [open]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await createBeneficiary(merchantId, {
        bank_account_id: Number(bankAccountId),
        name: name.trim(),
        account_no: accountNo.trim(),
        ifsc: ifsc.trim().toUpperCase(),
        email: email.trim(),
        phone: phone.trim(),
      });
      // Invalidate BOTH cache shapes so the new row appears in every
      // surface that lists beneficiaries (MerchantDetail card + new
      // /beneficiaries page + UploadBatchModal's validation pre-load).
      await qc.invalidateQueries({
        queryKey: ["merchant-beneficiaries", merchantId],
      });
      await qc.invalidateQueries({
        queryKey: ["beneficiaries", merchantId],
      });
      onClose();
    } catch (err) {
      // PDF round 2 item 2: when the backend returns a customer-duplicate
      // 422, surface the colliding field + existing customer id verbatim
      // so the operator knows exactly which existing customer to
      // reconcile. Falls through to the generic mapper otherwise.
      const dup = extractCustomerDuplicate(err);
      if (dup !== null) {
        const label = dup.field === "email" ? "email address" : "phone number";
        setError(
          `This ${label} ("${dup.value}") is already attached to customer #${dup.existing_customer_id} on this merchant. ` +
            `Open the existing customer record and add this account there, or correct the ${dup.field} on this form.`,
        );
      } else {
        setError(
          apiErrorMessage(
            err,
            "Could not register the beneficiary — the provider rejected the request, or the selected rail's credentials are invalid.",
          ),
        );
      }
      setSaving(false);
    }
  }

  // PDF review 26 May 26 item B: Indian bank account numbers are 9–18 digits
  // (no national fixed length — varies per bank). Catch out-of-range or
  // non-numeric values here so the provider doesn't bounce them with a
  // generic error. IFSC is fixed-format (RBI mandate: 11 chars,
  // BANK + "0" + branch code) — pre-check so the submit button stays
  // disabled rather than fire-and-fail.
  const accountNoValid = /^\d{9,18}$/.test(accountNo.trim());
  const ifscValid = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.trim().toUpperCase());

  const canSubmit =
    !saving &&
    bankAccountId !== "" &&
    name.trim() !== "" &&
    accountNoValid &&
    ifscValid &&
    email.trim() !== "" &&
    phone.trim() !== "";

  const noRails = rails.length === 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={saving}
      title="Add beneficiary"
      foot={
        noRails ? (
          <button className="btn" type="button" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button
              className="btn ghost"
              type="button"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              className="btn accent"
              type="submit"
              form="add-bene-form"
              disabled={!canSubmit}
            >
              {saving ? "Registering…" : "Register beneficiary"}
            </button>
          </>
        )
      }
    >
      {noRails ? (
        <div className="dim" style={{ fontSize: 13 }}>
          This merchant needs at least one bank rail before a beneficiary can
          be registered with the provider.
        </div>
      ) : (
        <form id="add-bene-form" onSubmit={handleSubmit} className="stack-16">
          <div className="field">
            <label className="label" htmlFor="ab-rail">
              Register via rail
            </label>
            <select
              id="ab-rail"
              className="input"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
            >
              <option value="">Select a rail…</option>
              {rails.map((r) => (
                // AUDIT-PASS-9: include provider so operators can distinguish
                // IDFC vs Buckzy when both rails exist on a merchant.
                <option key={r.id} value={r.id}>
                  {r.label} · {providerLabel(r.provider)} (ba_{r.id})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="ab-name">
              Beneficiary name
            </label>
            <input
              id="ab-name"
              className="input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="ab-acct">
              Account number
            </label>
            {/* Strip whitespace on input — Indian bank statements sometimes
                group digits with spaces ("12 3456 789012"), and operators
                copy-paste from them. The provider rejects non-numeric
                content; pre-strip so the field always shows what'll
                actually be sent. inputMode="numeric" surfaces the numeric
                keyboard on mobile. */}
            <input
              id="ab-acct"
              className="input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={accountNo}
              onChange={(e) => setAccountNo(e.target.value.replace(/\s+/g, ""))}
              aria-invalid={accountNo !== "" && !accountNoValid}
            />
            {accountNo !== "" && !accountNoValid && (
              <div
                className="dim"
                style={{ fontSize: 12, marginTop: 4, color: "var(--bad)" }}
              >
                Indian account numbers are 9–18 digits.
              </div>
            )}
          </div>
          <div className="field">
            <label className="label" htmlFor="ab-ifsc">
              IFSC
            </label>
            {/* AUDIT-PASS-5 UI #13: IFSC codes are uppercase-canonical
                (RBI mandate — "HDFC0001234"). The IDFC provider rejects
                mixed/lowercase codes with a validation error. Uppercase on
                input rather than waiting for the trim+toUpperCase in
                handleSubmit so the operator sees what will actually be
                sent. autoCapitalize="characters" gives the mobile keyboard
                the right hint. Whitespace stripped so a copy-paste from a
                spreadsheet with stray padding doesn't make the field look
                invalid for no reason. */}
            <input
              id="ab-ifsc"
              className="input"
              type="text"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={11}
              value={ifsc}
              onChange={(e) =>
                setIfsc(e.target.value.replace(/\s+/g, "").toUpperCase())
              }
              placeholder="HDFC0000001"
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="ab-email">
              Email <span style={{ color: "var(--ink-3)" }}>· required</span>
            </label>
            <input
              id="ab-email"
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="ab-phone">
              Phone <span style={{ color: "var(--ink-3)" }}>· required</span>
            </label>
            <input
              id="ab-phone"
              className="input"
              type="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          {error && (
            <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
              {error}
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}
